import React, { useEffect, useMemo } from 'react';
import { useForm, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { InputText, StyledSelect, ErrorField } from '~/lib/HooksFormFields';
import BackButton from '~/components/BackButton';
import { useExhibitions } from '~/hooks/exhibitions';
import { useCreateCompany, useGetCompany, useUpdateCompany } from '~/hooks/companies';
import { IOption } from '~/types/options';

import styles from './company-form.module.scss';

type Props = {
  companyType: 'fournisseur',
  companyId?: string,
};

function exhibitionIdFromCompany(company: any): string | null {
  const raw = company?.exhibitions?.[0];
  if (!raw) return null;
  if (typeof raw === 'object' && raw !== null && '_id' in raw) {
    return String((raw as { _id: string })._id);
  }
  return String(raw);
}

export default function CompanyForm({ companyType, companyId }: Props) {
  const navigate = useNavigate();
  const isEditMode = Boolean(companyId);
  const { data: exhibitionsData } = useExhibitions();
  const exhibitions = exhibitionsData?.exhibitions || [];

  const { data: company, isLoading: isCompanyLoading } = useGetCompany(companyId || '');

  const {
    mutateAsync: createCompany,
    isLoading: isCreating,
  } = useCreateCompany();

  const {
    mutateAsync: updateCompany,
    isLoading: isUpdating,
  } = useUpdateCompany();

  const exhibitionOptions: IOption[] = useMemo(
    () => exhibitions.map((ex: any) => ({ label: ex.name, value: ex._id })),
    [exhibitions],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      exhibition: null as string | null,
    },
  });

  useEffect(() => {
    if (!isEditMode || !company) return;
    reset({
      name: company.name || '',
      exhibition: exhibitionIdFromCompany(company),
    });
  }, [company, isEditMode, reset]);

  const errorsForm: any = errors;

  const onSubmit = async (data: FieldValues) => {
    if (isCreating || isUpdating) return;

    if (isEditMode && companyId) {
      await updateCompany({
        id: companyId,
        payload: {
          name: data.name,
          exhibitions: data.exhibition ? [data.exhibition] : [],
        },
      });
      navigate(`/fournisseurs/${companyId}/membres`);
      return;
    }

    const res: any = await createCompany({
      name: data.name,
      type: companyType,
      exhibitions: data.exhibition ? [data.exhibition] : [],
    });

    if (res?.company?._id) {
      navigate(`/fournisseurs/${res.company._id}`);
    } else {
      navigate('/fournisseurs');
    }
  };

  if (isEditMode && (isCompanyLoading || !company)) {
    return <p>Loading</p>;
  }

  return (
    <div className={styles.form}>
      <BackButton path={isEditMode && companyId ? `/fournisseurs/${companyId}/membres` : undefined} />
      <section>
        <h1 className={styles.titleCreate}>
          {isEditMode ? 'Modifier le fournisseur' : 'Créer un fournisseur'}
        </h1>
        <div className={styles.row}>
          <div className={styles.containerField}>
            <InputText
              label="Nom"
              name="name"
              control={control as any}
              rules={{
                required: 'Ce champs est obligatoire',
              }}
            />
            {errorsForm?.name && (
              <ErrorField message={errorsForm.name.message} />
            )}
          </div>
          <div className={styles.containerField}>
            <StyledSelect
              label="Salon"
              name="exhibition"
              control={control as any}
              options={exhibitionOptions}
              placeholder="Choisir un salon"
              isSearchable
            />
          </div>
        </div>

        <div className={styles.actions}>
          <button type="button" onClick={handleSubmit(onSubmit)}>
            {isEditMode ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </section>
    </div>
  );
}
